import React, { useState } from 'react';
import { useSetBreadcrumbs } from 'works/legacy/lib/client-store';
import { ORGANIZATION_ADMIN_MENU } from 'works/legacy/pages/organization/admin-settings/organization-admin-vertical-nav-menu';
import { PageSteps } from '@common/components';
import { SuppliersManagementBulkLocationsUpdateStepUnits } from './suppliers-management-bulk-locations-update-step-units';
import { SuppliersManagementBulkLocationsUpdateStepReview } from './suppliers-management-bulk-locations-update-step-review';
import { SuppliersManagementBulkLocationsUpdateStepSuppliers } from './suppliers-management-bulk-locations-update-step-suppliers';
import {
  BulkUpdateSupplierDepartments,
  BulkUpdateSupplierDepartmentsResponse,
  BulkUpdateSupplierDepartmentsVariables,
  ORGANIZATION_UNITS_QUERY,
} from '@institutions/graphql';
import { SupplierType } from '@institutions/types';
import { SupplierStatusTypes } from '@common/graphql';
import { gql, useMutation, useQuery, useNavigate } from '@common/hooks';
import { createMessage } from 'works/legacy/lib/message-helper';

const UnitBulkSupplierPermissionsQuery = gql`
  query UnitBulkSupplierPermissions($suppliersInput: SuppliersInput!) {
    suppliers(input: $suppliersInput) {
      id
      name
      statusForCurrentOrganization {
        id
        status
      }
    }
  }
`;

type UnitBulkSupplierPermissionsVariables = {
  suppliersInput: {
    filters?: {
      status?: SupplierStatusTypes[];
    };
  };
};

type UnitBulkSupplierPermissionsResponse = {
  suppliers: SupplierType[];
  organizationBusinesses: {
    id: string;
    name: string;
    organizationUnits: {
      id: string;
      suppliers: {
        id: string;
      }[];
    }[];
  }[];
};

export function SuppliersManagementBulkLocationsUpdateSteps() {
  useSetBreadcrumbs([
    {
      name: 'Admin',
      path: '/works/admin',
    },
    {
      name: 'Suppliers',
      path: ORGANIZATION_ADMIN_MENU.SUPPLIERS.ROUTE,
    },
    {
      name: 'Update',
      path: `${ORGANIZATION_ADMIN_MENU.SUPPLIERS.ROUTE}/bulk-locations-update`,
    },
  ]);

  const navigate = useNavigate();

  const [selectedSuppliersIds, setSelectedSuppliersIds] = useState<string[]>(
    []
  );
  const [selectedUnitsIds, setSelectedUnitsIds] = useState<string[]>([]);
  const [notes, setNotes] = useState('');

  const {
    data: suppliersData,
    loading: suppliersLoading,
    error: suppliersError,
  } = useQuery<
    UnitBulkSupplierPermissionsResponse,
    UnitBulkSupplierPermissionsVariables
  >(UnitBulkSupplierPermissionsQuery, {
    variables: {
      suppliersInput: {
        filters: {
          status: ['ACTIVE', 'PENDING_ACTIVATION'],
        },
      },
    },
  });

  const {
    data: unitsData,
    loading: unitsLoading,
    error: unitsError,
  } = useQuery(ORGANIZATION_UNITS_QUERY, {
    variables: {
      input: {
        scope: 'department',
      },
    },
  });

  const organizationUnits = unitsData?.organizationUnits || [];
  const suppliers = suppliersData?.suppliers || [];

  const [bulkUpdateSupplierDepartmentsQuery, { loading: confirmLoading }] =
    useMutation<
      BulkUpdateSupplierDepartmentsResponse,
      BulkUpdateSupplierDepartmentsVariables
    >(BulkUpdateSupplierDepartments);

  async function onConfirm() {
    const { data, errors } = await bulkUpdateSupplierDepartmentsQuery({
      variables: {
        input: {
          organizationUnitIds: selectedUnitsIds,
          supplierIds: selectedSuppliersIds,
          notes: notes,
        },
      },
    });

    if (errors) {
      createMessage('error', errors.map(({ message }) => message).join(' '));
      return;
    }

    const response = data?.bulkUpdateSupplierDepartments.success;

    if (response) {
      createMessage('success', 'Locations successfully saved');
      navigate(ORGANIZATION_ADMIN_MENU.SUPPLIERS.ROUTE);
    }
  }

  return (
    <PageSteps
      steps={[
        {
          title: 'Supplier Selection',
          page: (
            <SuppliersManagementBulkLocationsUpdateStepSuppliers
              onSelectSupplierIdsChange={setSelectedSuppliersIds}
              selectedSupplierIds={selectedSuppliersIds}
              suppliers={suppliers}
              loading={suppliersLoading}
              error={suppliersError}
            />
          ),
          forwardButtonDisabled:
            suppliersLoading || !selectedSuppliersIds.length,
        },
        {
          title: 'Units',
          page: (
            <SuppliersManagementBulkLocationsUpdateStepUnits
              onSelectUnitIdsChange={setSelectedUnitsIds}
              selectedUnitIds={selectedUnitsIds}
              organizationUnits={organizationUnits}
              loading={unitsLoading}
              error={unitsError}
            />
          ),
          forwardButtonDisabled: unitsLoading || !selectedUnitsIds.length,
        },
        {
          title: 'Summary',
          page: (
            <SuppliersManagementBulkLocationsUpdateStepReview
              selectedSuppliersIds={selectedSuppliersIds}
              suppliers={suppliers}
              selectedUnitsIds={selectedUnitsIds}
              organizationUnits={organizationUnits}
              onNotesChange={setNotes}
            />
          ),
          forwardButtonDisabled: !notes.length,
          confirmButtonText: 'Confirm',
        },
      ]}
      confirmLoading={confirmLoading}
      onConfirm={onConfirm}
    />
  );
}
